import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeContextProvider, useThemeContext } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { Layout } from './components/common/Layout';
import { ErrorBoundary } from './components/common/ErrorBoundary';

// Pages (lazy loaded)
import { Suspense, lazy } from 'react';
import { LoadingSpinner } from './components/common/LoadingSpinner';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'));
const ActivateDevicePage = lazy(() => import('./pages/ActivateDevicePage'));
const HomePage = lazy(() => import('./pages/HomePage'));
const UserSettingsPage = lazy(() => import('./pages/UserSettingsPage'));
const ConnectionsPage = lazy(() => import('./pages/ConnectionsPage'));
const SemanticModelsPage = lazy(() => import('./pages/SemanticModelsPage'));
const NewSemanticModelPage = lazy(() => import('./pages/NewSemanticModelPage'));
const SemanticModelDetailPage = lazy(() => import('./pages/SemanticModelDetailPage'));
const OntologiesPage = lazy(() => import('./pages/OntologiesPage'));
const OntologyDetailPage = lazy(() => import('./pages/OntologyDetailPage'));
const DataAgentPage = lazy(() => import('./pages/DataAgentPage'));
const SystemSettingsPage = lazy(() => import('./pages/SystemSettingsPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const SharedChatPage = lazy(() => import('./pages/SharedChatPage'));
const DataImportsPage = lazy(() => import('./pages/DataImportsPage'));
const NewDataImportPage = lazy(() => import('./pages/NewDataImportPage'));
const DataImportDetailPage = lazy(() => import('./pages/DataImportDetailPage'));

// Test login page (development only)
const TestLoginPage = import.meta.env.PROD
  ? null
  : lazy(() => import('./pages/TestLoginPage'));

function AppRoutes() {
  const { theme } = useThemeContext();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <Suspense fallback={<LoadingSpinner fullScreen />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/share/:shareToken" element={<SharedChatPage />} />

            {/* Test login (development only) */}
            {!import.meta.env.PROD && TestLoginPage && (
              <Route path="/testing/login" element={<TestLoginPage />} />
            )}

            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
              {/* Device activation page - without layout for full-screen experience */}
              <Route path="/activate" element={<ActivateDevicePage />} />

              <Route element={<Layout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/settings" element={<UserSettingsPage />} />
                <Route path="/connections" element={<ConnectionsPage />} />
                <Route path="/semantic-models" element={<SemanticModelsPage />} />
                <Route path="/semantic-models/new" element={<NewSemanticModelPage />} />
                <Route path="/semantic-models/:id" element={<SemanticModelDetailPage />} />
                <Route path="/ontologies" element={<OntologiesPage />} />
                <Route path="/ontologies/:id" element={<OntologyDetailPage />} />
                <Route path="/agent" element={<DataAgentPage />} />
                <Route path="/agent/:chatId" element={<DataAgentPage />} />
                <Route path="/data-imports" element={<DataImportsPage />} />
                <Route path="/data-imports/new" element={<NewDataImportPage />} />
                <Route path="/data-imports/:id" element={<DataImportDetailPage />} />
                <Route path="/admin/users" element={<UserManagementPage />} />
                <Route path="/admin/settings" element={<SystemSettingsPage />} />
              </Route>
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <ThemeContextProvider>
      <AuthProvider>
        <NotificationProvider>
          <AppRoutes />
        </NotificationProvider>
      </AuthProvider>
    </ThemeContextProvider>
  );
}
